import { useContext, useEffect, useState } from 'react';
import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';

import { AppContext } from '../../../App.context';

export function useProducts(): GetProductsResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);
	const [products, setProducts] = useState<Product[]>([]);

	const cacheTime = 1000 * 60 * 30;

	const { data, isPending, error } = useQuery<ProductsQuery>({
		queryKey: ['products'],
		queryFn: async () => graphQLClient.request(productsQueryDocument),
		staleTime: cacheTime,
		refetchInterval: cacheTime,
		gcTime: cacheTime,
	});

	useEffect(() => {
		if (!data) return;

		const pricesMap: Map<string, Array<Price>> = data.prices.reduce(
			reducePrices,
			new Map(),
		);

		setProducts(
			data.products
				.map(mapProducts)
				.map(p => {
					p.prices = pricesMap.get(p.id) || [];
					return p;
				})
				.filter(p => p.prices.length),
		);
	}, [data]);

	return {
		products,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

interface ProductsQuery {
	products: Product[];
	prices: Price[];
}

interface GetProductsResponse {
	products: Array<Product>;
	isLoading: boolean;
	error: Error | null;
}

function mapProducts(product: any): Product {
	const { id, active, name, description, images } = product;
	return {
		id,
		active,
		name,
		description,
		images,
		prices: [],
	};
}

function reducePrices(
	acc: Map<string, Array<Price>>,
	p: any,
): Map<string, Array<Price>> {
	const {
		id,
		active,
		currency,
		unitAmount,
		metadata,
		nickname,
		type,
		product,
		recurring,
	} = p;
	if (type !== 'recurring') {
		return acc;
	}
	const prices: Array<Price> = acc.get(product) || [];
	const price: Price = {
		id,
		active,
		currency,
		unitAmount,
		metadata,
		nickname,
		type,
		product,
		recurring:
			recurring === null
				? null
				: {
						interval: recurring.interval,
						intervalCount: recurring.intervalCount,
						meter: recurring.meter,
						trialPeriodDays: recurring.trialPeriodDays,
				  },
	};
	prices.push(price);
	acc.set(product, prices);
	return acc;
}

export interface Product {
	id: string;
	active: boolean;
	description: string;
	name: string;
	images: Array<string>;
	prices: Array<Price>;
}

export interface Price {
	id: string;
	active: boolean;
	currency: string;
	metadata: any;
	nickname: string | null;
	type: 'one_time' | 'recurring';
	product: string | null;
	recurring: Recurring | null;
	unitAmount: number | null;
}

export interface Recurring {
	interval: 'day' | 'month' | 'week' | 'year';
	intervalCount: number;
	meter: string | null;
	trialPeriodDays: number | null;
}

const productsQueryDocument = gql`
	query Products {
		products {
			id
			active
			description
			name
			images
		}

		prices {
			id
			active
			currency
			unitAmount
			metadata
			nickname
			type
			product
			recurring {
				interval
				intervalCount
				trialPeriodDays
			}
		}
	}
`;
